import { useEffect, useRef, useState } from "react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { UploadCloud, ShieldCheck, ShieldAlert, FileCheck2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
    { value: "pan", label: "PAN Card" },
    { value: "aadhaar", label: "Aadhaar" },
    { value: "salary_slip", label: "Salary Slip" },
    { value: "bank_statement", label: "Bank Statement" },
    { value: "photo_id", label: "Photo ID" },
    { value: "other", label: "Other" },
];

export default function DocumentVerification() {
    const [docs, setDocs] = useState([]);
    const [type, setType] = useState("pan");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef();

    const load = async () => {
        try { const { data } = await api.get("/documents"); setDocs(data); } catch {}
    };
    useEffect(() => { load(); }, []);

    const pick = (f) => {
        if (!f) return;
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const submit = async () => {
        if (!file) return toast.error("Please choose an image first");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("doc_type", type);
            fd.append("file", file);
            const { data } = await api.post("/documents/verify", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(data.is_valid ? "Document verified" : "Verification returned issues");
            setFile(null);
            setPreview(null);
            load();
        } catch (err) {
            toast.error(formatDetail(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="documents-page">
            <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">AI-Powered</p>
                <h1 className="font-serif text-3xl text-komma-navy">Document Verification</h1>
                <p className="text-sm text-gray-600 mt-1">Upload identity or financial documents. Our AI extracts fields and flags anomalies.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="komma-card p-6 lg:col-span-1">
                    <div className="flex items-center gap-2 mb-4">
                        <Sparkles size={18} className="text-komma-gold" />
                        <p className="text-[10px] uppercase tracking-[0.24em] text-komma-navy">Upload & Verify</p>
                    </div>
                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Document Type</Label>
                    <Select value={type} onValueChange={setType}>
                        <SelectTrigger className="mt-2 h-11" data-testid="doc-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                    </Select>

                    <div
                        className="mt-5 border-2 border-dashed border-komma-navy/20 rounded-lg p-6 text-center cursor-pointer hover:border-komma-gold transition-colors"
                        onClick={() => inputRef.current?.click()}
                        data-testid="doc-dropzone"
                    >
                        {preview ? (
                            <img src={preview} alt="preview" className="max-h-40 mx-auto rounded" />
                        ) : (
                            <>
                                <UploadCloud className="mx-auto text-komma-gold" size={32} />
                                <p className="mt-2 text-sm text-komma-navy">Click to upload image</p>
                                <p className="text-xs text-gray-500 mt-1">JPEG / PNG / WEBP · up to 8 MB</p>
                            </>
                        )}
                        <input ref={inputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => pick(e.target.files?.[0])} data-testid="doc-file-input" />
                    </div>

                    <Button onClick={submit} disabled={busy || !file} className="mt-5 w-full bg-komma-navy hover:bg-komma-navy-light" data-testid="doc-verify-btn">
                        {busy ? "Analysing…" : "Verify Document"}
                    </Button>
                </div>

                <div className="lg:col-span-2 space-y-3" data-testid="doc-results-list">
                    {docs.length === 0 ? (
                        <div className="komma-card p-10 text-center">
                            <FileCheck2 className="mx-auto text-komma-gold" size={40} />
                            <p className="font-serif text-xl text-komma-navy mt-3">No documents verified yet</p>
                            <p className="text-sm text-gray-500 mt-1">Upload a PAN, Aadhaar, salary slip or bank statement to begin.</p>
                        </div>
                    ) : (
                        docs.map((d) => {
                            const valid = d.is_valid;
                            const conf = Math.round((d.confidence || 0) * 100);
                            return (
                                <div key={d.id} className="komma-card p-5">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">{d.doc_type.replace("_", " ")}</p>
                                            <p className="font-serif text-lg text-komma-navy mt-1">{d.filename}</p>
                                            <p className="text-xs text-gray-500">By {d.uploaded_by_name} · {new Date(d.created_at).toLocaleString()}</p>
                                        </div>
                                        <Badge variant="outline" className={valid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}>
                                            {valid ? <><ShieldCheck size={12} className="mr-1" /> Valid</> : <><ShieldAlert size={12} className="mr-1" /> Issues</>}
                                            <span className="ml-2">{conf}%</span>
                                        </Badge>
                                    </div>
                                    <div className="komma-gold-hairline my-4" />
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        {Object.entries(d.extracted || {}).filter(([k]) => !["is_valid", "confidence", "issues", "raw"].includes(k)).map(([k, v]) => (
                                            <div key={k} className="flex justify-between border-b border-gray-100 py-1">
                                                <span className="text-gray-500 uppercase text-[10px] tracking-widest">{k.replace(/_/g, " ")}</span>
                                                <span className="text-komma-navy font-medium truncate max-w-[60%] text-right">{String(v)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {(d.issues || []).length > 0 && (
                                        <div className="mt-3 p-3 rounded bg-red-50 border border-red-100">
                                            <p className="text-[10px] uppercase tracking-widest text-red-700">Issues</p>
                                            <ul className="mt-1 text-xs text-red-700 list-disc list-inside">
                                                {(d.issues || []).map((iss, i) => <li key={i}>{iss}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
